<template>
  <div class="site-create-step3" @click="closeContextMenu">
    <div class="form-card">
      <div class="viewer-wrap">
        <div
          class="drawing-stage"
          ref="stageRef"
          @contextmenu.prevent="openContextMenu"
          @mousemove="onLoggerDrag"
          @mouseup="endLoggerDrag"
          @mouseleave="endLoggerDrag"
        >
          <div
            v-if="preview"
            ref="imageBoxRef"
            class="image-box"
            :style="imageBoxStyle"
          >
            <img
              :src="preview.url"
              ref="imageRef"
              class="preview-full"
              alt="drawing"
              draggable="false"
            />

            <div
              v-for="logger in loggers"
              :key="logger.id"
              class="logger-marker"
              :style="getLoggerStyle(logger)"
              @mousedown.stop="startLoggerDrag($event, logger)"
              @click.stop="selectLogger(logger)"
              @contextmenu.prevent.stop="openLoggerContextMenu($event, logger)"
            >
              <div class="logger-dot"></div>
              <div class="logger-label">{{ logger.name }}</div>
            </div>
          </div>

          <div v-else class="drawing-empty">
            등록된 도면이 없습니다.
          </div>

          <!-- 우클릭 메뉴 -->
          <div
            v-if="showContextMenu"
            class="context-menu"
            :style="{ left: contextMenuX + 'px', top: contextMenuY + 'px' }"
            @click.stop
          >
            <button class="context-item" @click="openAddLoggerModal">
              로거추가
            </button>
            <button class="context-item" :disabled="!selectedLogger" @click="openEditLoggerModal">
              수정
            </button>
            <button class="context-item danger" :disabled="!selectedLogger" @click="deleteLogger">
              삭제
            </button>
          </div>

          <!-- 로거 추가/수정 모달 -->
          <div v-if="showLoggerModal" class="modal-backdrop" @click.self="closeLoggerModal">
            <div class="logger-modal">
              <div class="modal-title">
                {{ modalMode === 'edit' ? '로거 수정' : '로거 추가' }}
              </div>

              <div class="form-grid">
                <div class="form-row full">
                  <label>기존 로거 선택</label>

                  <div class="logger-filter-tabs">
                    <button
                      type="button"
                      class="logger-filter-btn"
                      :class="{ active: loggerFilterType === 'all' }"
                      @click="loggerFilterType = 'all'"
                    >
                      전체
                    </button>

                    <button
                      type="button"
                      class="logger-filter-btn"
                      :class="{ active: loggerFilterType === 'unassigned' }"
                      @click="loggerFilterType = 'unassigned'"
                    >
                      미연동
                    </button>
                  </div>

                  <div class="logger-card-list">

                    <div
                      v-for="item in filteredAvailableLoggers"
                      :key="item.id"
                      class="logger-card"
                      :class="{
                        active: String(loggerForm.sensorLoggerId) === String(item.id)
                      }"
                      @click="loggerForm.sensorLoggerId = item.id"
                    >
                      <div class="logger-card-title">
                        {{ item.loggerName || '-' }}
                      </div>

                      <div class="logger-card-info">
                        <span class="info-label">라인번호</span>
                        <span class="info-value">
                          {{ item.lineNumber || '-' }}
                        </span>
                      </div>

                      <div class="logger-card-info">
                        <span class="info-label">시리얼번호</span>
                        <span class="info-value">
                          {{ item.serialNumber || '-' }}
                        </span>
                      </div>

                      <div class="logger-card-info">
                        <span class="info-label">연동현장</span>
                        <span class="info-value">
                          {{ item.siteName || '미연동' }}
                        </span>
                      </div>
                    </div>

                    <div
                      v-if="!filteredAvailableLoggers.length"
                      class="logger-card-empty"
                    >
                      선택 가능한 로거가 없습니다.
                    </div>

                  </div>
                </div>

                <div class="form-row">
                  <label>현장 로거명</label>
                  <input
                    v-model="loggerForm.name"
                    type="text"
                    class="form-control"
                    placeholder="현장에 표시할 로거명을 입력하세요"
                  />
                </div>

                <div class="form-row">
                  <label>라인번호</label>
                  <input
                    :value="selectedLoggerMaster ? selectedLoggerMaster.lineNumber : ''"
                    type="text"
                    class="form-control"
                    readonly
                  />
                </div>

                <div class="form-row">
                  <label>시리얼번호</label>
                  <input
                    :value="selectedLoggerMaster ? selectedLoggerMaster.serialNumber : ''"
                    type="text"
                    class="form-control"
                    readonly
                  />
                </div>

                <div class="form-row">
                  <label>로거타입</label>
                  <input
                    :value="selectedLoggerMaster ? selectedLoggerMaster.type : ''"
                    type="text"
                    class="form-control"
                    readonly
                  />
                </div>

                <div class="form-row full">
                  <label>설명</label>
                  <textarea
                    v-model="loggerForm.description"
                    class="form-control textarea"
                    placeholder="설명을 입력하세요"
                  ></textarea>
                </div>
              </div>

              <div class="modal-actions">
                <button class="btn" @click="closeLoggerModal">취소</button>
                <button class="btn primary" @click="saveLogger">저장</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="bottom-actions-page">
        <button class="btn secondary" type="button" @click="onBack">이전으로</button>
        <button class="btn primary" type="button" @click="onNext">다음으로</button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'
import { clampPx } from '@/util/drawingPosition'

export default {
  name: 'CreateSiteStep3',

  props: {
    formValue: {
      type: Object,
      default() {
        return {}
      },
    },
  },

  data() {
    return {
      preview: null,

      showContextMenu: false,
      contextMenuX: 0,
      contextMenuY: 0,
      clickedX: 0,
      clickedY: 0,

      showLoggerModal: false,
      modalMode: 'add',

      selectedLoggerId: null,

      availableLoggers: [],
      loggerFilterType: 'all',

      loggerForm: {
        sensorLoggerId: null,
        name: '',
        description: '',
      },

      loggers: [],
      draggingLoggerId: null,
      dragOffsetX: 0,
      dragOffsetY: 0,

      ownedPreviewUrls: new Set(),
      restorePreviewToken: 0,

      imageBoxWidth: 1400,
      imageBoxHeight: 900,
    }
  },

  computed: {
    selectedLogger() {
      return this.loggers.find(item => item.id === this.selectedLoggerId) || null
    },

    selectedLoggerMaster() {
      return this.availableLoggers.find(
        item => String(item.id) === String(this.loggerForm.sensorLoggerId)
      ) || null
    },

    filteredAvailableLoggers() {
      if (this.loggerFilterType === 'unassigned') {
        return this.availableLoggers.filter(
          item => !item.siteId
        )
      }

      return this.availableLoggers
    },

    imageBoxStyle() {
      return {
        width: `${this.imageBoxWidth}px`,
        height: `${this.imageBoxHeight}px`,
        margin: '0 auto',
      }
    },
  },

  mounted() {
    this.restorePreviewFromForm()
    window.addEventListener('click', this.closeContextMenu)
    this.loadAvailableLoggers()
  },

  beforeUnmount() {
    this.revokeOwnedPreviewUrls()
    window.removeEventListener('click', this.closeContextMenu)
  },

  watch: {
    formValue: {
      deep: true,
      immediate: true,
      handler() {
        this.restorePreviewFromForm()
      },
    },
  },

  methods: {
    openLoggerContextMenu(event, logger) {
      this.selectedLoggerId = logger.id
      this.contextMenuX = event.clientX
      this.contextMenuY = event.clientY
      this.showContextMenu = true
    },

    createOwnedPreviewUrl(file) {
      const url = URL.createObjectURL(file)
      this.ownedPreviewUrls.add(url)
      return url
    },

    revokeOwnedPreviewUrl(url) {
      if (!url) return
      if (this.ownedPreviewUrls.has(url)) {
        URL.revokeObjectURL(url)
        this.ownedPreviewUrls.delete(url)
      }
    },

    revokeOwnedPreviewUrls() {
      for (const url of this.ownedPreviewUrls) {
        URL.revokeObjectURL(url)
      }
      this.ownedPreviewUrls.clear()
    },

    restorePreviewFromForm() {
      const drawing = this.formValue?.drawing ?? null
      const drawingAttachFileNo = this.formValue?.drawingAttachFileNo ?? null

      this.restorePreviewToken += 1
      const currentToken = this.restorePreviewToken

      if (this.preview?.isNew) {
        this.revokeOwnedPreviewUrl(this.preview.url)
      }

      if (drawing instanceof File) {
        const url = this.createOwnedPreviewUrl(drawing)
        const img = new Image()

        img.onload = async () => {
          if (currentToken !== this.restorePreviewToken) return

          this.preview = {
            file: drawing,
            url,
            name: drawing.name || 'drawing',
            size: drawing.size || 0,
            imageWidth: img.naturalWidth,
            imageHeight: img.naturalHeight,
            isNew: true,
          }

          this.imageBoxWidth = img.naturalWidth || 1400
          this.imageBoxHeight = img.naturalHeight || 900

          if (this.formValue?.siteId) {
            await this.loadStep3Detail()
          }
        }

        img.onerror = () => {
          if (currentToken !== this.restorePreviewToken) return
          this.preview = null
        }

        img.src = url
        return
      }

      if (drawingAttachFileNo) {
        const url = `/app/file/image/${drawingAttachFileNo}`
        const img = new Image()

        img.onload = async () => {
          if (currentToken !== this.restorePreviewToken) return

          this.preview = {
            file: null,
            url,
            name: '기존 도면',
            size: 0,
            imageWidth: img.naturalWidth,
            imageHeight: img.naturalHeight,
            isNew: false,
          }

          this.imageBoxWidth = img.naturalWidth || 1400
          this.imageBoxHeight = img.naturalHeight || 900

          if (this.formValue?.siteId) {
            await this.loadStep3Detail()
          }
        }

        img.onerror = () => {
          if (currentToken !== this.restorePreviewToken) return
          this.preview = null
        }

        img.src = url
        return
      }

      this.preview = null
    },

    async loadAvailableLoggers() {
      try {
        const res = await api.get('/app/sensorLogger/list', {
          params: {
            deleted: false,
            orderBy: 'createdAtDesc',
            page: 0,
            size: 1000,
          },
        })

        this.availableLoggers = res.data?.result?.content || []
      } catch (e) {
        console.error('logger list load failed', e)
        this.availableLoggers = []
      }
    },

    normalizeServerPoint(value, fullSize) {
      const num = Number(value ?? 0)

      if (num >= 0 && num <= 100) {
        return Math.round((num / 100) * fullSize)
      }

      return Math.round(num)
    },

    normalizeSensorForSave(sensor) {
      return {
        sensorSiteId: sensor.sensorSiteId || null,
        sensorId: sensor.sensorId,
        sensorCode: sensor.sensorCode || '',
        serialNumber: sensor.serialNumber || '',
        sortOrder: sensor.sortOrder || 0,
        sensorTypeId: sensor.sensorTypeId || null,
        configValues: sensor.configValues || {},
      }
    },

    async loadStep3Detail() {
      if (!this.formValue?.siteId) return

      try {
        const res = await api.get('/app/site/step3/detail', {
          params: { siteId: this.formValue.siteId },
        })

        const result = res.data?.result
        if (!result || !Array.isArray(result.loggerItems)) {
          this.loggers = []
          return
        }

        this.loggers = result.loggerItems.map(item => ({
          id: item.sensorLoggerSiteId || `loaded_${item.sensorLoggerId}_${Math.random()}`,
          sensorLoggerSiteId: item.sensorLoggerSiteId,
          sensorLoggerId: item.sensorLoggerId,
          masterType: item.loggerType,
          isMultiSensorLogger: item.isMultiSensorLogger === true,
          multiSensorType: item.multiSensorType || null,
          serialNumber: item.loggerSerialNumber,
          lineNumber: item.loggerLineNumber,
          name: item.name,
          description: item.description,
          x: this.normalizeServerPoint(item.positionX, this.imageBoxWidth),
          y: this.normalizeServerPoint(item.positionY, this.imageBoxHeight),

          // 센서 설정은 이 화면에서 수정하지 않지만,
          // 기존 sensor_site / sensor_config_value 저장값이 다음 저장 때 삭제되지 않도록 그대로 보존한다.
          sensors: (item.sensors || [])
            .sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999))
            .map((sensor, index) => ({
              sensorSiteId: sensor.sensorSiteId,
              sensorId: sensor.sensorId,
              sensorCode: sensor.sensorCode || '',
              serialNumber: sensor.serialNumber || '',
              sortOrder: sensor.sortOrder ?? index + 1,
              sensorTypeId: sensor.sensorTypeId ?? null,
              configValues: sensor.configValues || {},
            })),
        }))
      } catch (e) {
        console.error('step3 detail load failed', e)
      }
    },

    getImagePoint(event) {
      const imageBox = this.$refs.imageBoxRef
      if (!imageBox) {
        return {
          x: 0,
          y: 0,
          valid: false,
        }
      }

      const rect = imageBox.getBoundingClientRect()

      const rawX = event.clientX - rect.left
      const rawY = event.clientY - rect.top

      const valid =
        rawX >= 0 &&
        rawX <= rect.width &&
        rawY >= 0 &&
        rawY <= rect.height

      return {
        x: clampPx(rawX, 0, this.imageBoxWidth),
        y: clampPx(rawY, 0, this.imageBoxHeight),
        valid,
      }
    },

    openContextMenu(event) {
      const point = this.getImagePoint(event)

      this.contextMenuX = event.clientX
      this.contextMenuY = event.clientY

      if (!point.valid) {
        this.showContextMenu = false
        return
      }

      this.clickedX = point.x
      this.clickedY = point.y
      this.showContextMenu = true

      const clickedLogger = this.findLoggerAtPosition(point.x, point.y)
      this.selectedLoggerId = clickedLogger ? clickedLogger.id : null
    },

    closeContextMenu() {
      this.showContextMenu = false
    },

    findLoggerAtPosition(x, y) {
      const hitRadius = 18

      return (
        this.loggers.find(logger => {
          // transform(-50%, -100%) 보정
          const markerCenterX = logger.x
          const markerCenterY = logger.y - 10

          const dx = x - markerCenterX
          const dy = y - markerCenterY

          return Math.sqrt(dx * dx + dy * dy) <= hitRadius
        }) || null
      )
    },

    resetLoggerForm() {
      this.loggerForm = {
        sensorLoggerId: null,
        name: '',
        description: '',
      }
    },

    openAddLoggerModal() {
      this.modalMode = 'add'
      this.resetLoggerForm()
      this.showLoggerModal = true
      this.closeContextMenu()
    },

    openEditLoggerModal() {
      if (!this.selectedLogger) return

      this.modalMode = 'edit'
      this.loggerForm = {
        sensorLoggerId: this.selectedLogger.sensorLoggerId,
        name: this.selectedLogger.name,
        description: this.selectedLogger.description,
      }
      this.showLoggerModal = true
      this.closeContextMenu()
    },

    closeLoggerModal() {
      this.showLoggerModal = false
      this.resetLoggerForm()
    },

    saveLogger() {
      if (!this.loggerForm.sensorLoggerId || !this.loggerForm.name) {
        alert('기존 로거 선택과 현장 로거명은 필수입니다.')
        return
      }

      const selectedMaster = this.availableLoggers.find(
        item => String(item.id) === String(this.loggerForm.sensorLoggerId)
      )

      if (!selectedMaster) {
        alert('선택한 로거 정보를 찾을 수 없습니다.')
        return
      }

      if (this.modalMode === 'add') {
        this.loggers.push({
          id: `temp_${Date.now()}_${Math.random()}`,
          sensorLoggerSiteId: null,
          sensorLoggerId: selectedMaster.id,
          masterType: selectedMaster.type,
          isMultiSensorLogger: selectedMaster.isMultiSensorLogger === true,
          multiSensorType: selectedMaster.multiSensorType || null,
          serialNumber: selectedMaster.serialNumber,
          lineNumber: selectedMaster.lineNumber,
          name: this.loggerForm.name,
          description: this.loggerForm.description,
          x: this.clickedX,
          y: this.clickedY,

          // 센서 등록/설정은 LoggerSensorManage.vue에서 처리
          sensors: [],
        })
      } else {
        const index = this.loggers.findIndex(item => item.id === this.selectedLoggerId)
        if (index !== -1) {
          this.loggers[index] = {
            ...this.loggers[index],
            sensorLoggerId: selectedMaster.id,
            masterType: selectedMaster.type,
            isMultiSensorLogger: selectedMaster.isMultiSensorLogger === true,
            multiSensorType: selectedMaster.multiSensorType || null,
            serialNumber: selectedMaster.serialNumber,
            lineNumber: selectedMaster.lineNumber,
            name: this.loggerForm.name,
            description: this.loggerForm.description,
          }
        }
      }

      this.closeLoggerModal()
    },

    deleteLogger() {
      if (!this.selectedLogger) return

      const ok = window.confirm('선택한 로거를 삭제하시겠습니까?')
      if (!ok) return

      this.loggers = this.loggers.filter(item => item.id !== this.selectedLoggerId)
      this.selectedLoggerId = null
      this.closeContextMenu()
    },

    selectLogger(logger) {
      this.selectedLoggerId = logger.id
    },

    getLoggerStyle(logger) {
      return {
        position: 'absolute',
        left: `${logger.x}px`,
        top: `${logger.y}px`,
        transform: 'translate(-50%, -100%)',
        zIndex: 20,
        cursor: 'move',
      }
    },

    async onNext() {
      if (!this.formValue?.siteId) {
        alert('siteId가 없습니다.')
        return
      }

      const payload = {
        siteId: this.formValue.siteId,
        loggerItems: this.loggers.map((logger, loggerIndex) => ({
          sensorLoggerSiteId: logger.sensorLoggerSiteId || null,
          sensorLoggerId: logger.sensorLoggerId,
          name: logger.name,
          description: logger.description,
          positionX: Number(((logger.x / this.imageBoxWidth) * 100).toFixed(4)),
          positionY: Number(((logger.y / this.imageBoxHeight) * 100).toFixed(4)),
          sortOrder: loggerIndex + 1,

          // 이 화면에서는 센서 설정을 수정하지 않는다.
          // 단, 기존 등록 센서와 설정값은 보존해서 저장한다.
          sensors: (logger.sensors || []).map((sensor, sensorIndex) => ({
            ...this.normalizeSensorForSave(sensor),
            sortOrder: sensorIndex + 1,
          })),
        })),
      }

      try {
        await api.post('/app/site/step3/save', payload)

        this.$emit('next', {
          loggers: this.loggers,
        })
      } catch (e) {
        console.error(e)
        alert('Step3 저장 실패')
      }
    },

    onBack() {
      this.$emit('back', {
        loggers: this.loggers,
      })
    },

    startLoggerDrag(event, logger) {
      if (!this.preview) return

      const point = this.getImagePoint(event)
      if (!point.valid) return

      this.draggingLoggerId = logger.id
      this.dragOffsetX = point.x - logger.x
      this.dragOffsetY = point.y - logger.y
    },

    onLoggerDrag(event) {
      if (!this.draggingLoggerId) return

      const point = this.getImagePoint(event)
      if (!point.valid) return

      const logger = this.loggers.find(l => l.id === this.draggingLoggerId)
      if (!logger) return

      logger.x = clampPx(point.x - this.dragOffsetX, 0, this.imageBoxWidth)
      logger.y = clampPx(point.y - this.dragOffsetY, 0, this.imageBoxHeight)
    },

    endLoggerDrag() {
      this.draggingLoggerId = null
      this.dragOffsetX = 0
      this.dragOffsetY = 0
    },
  },
}
</script>

<style scoped>
.site-create-step3 {
  --bg: #ffffff;
  --text: #111827;
  --line2: #d1d5db;

  background: var(--bg);
  padding: 5px 22px 28px;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;

  height: 100%;
  min-height: 0;
}

.form-card {
  margin-top: 12px;

  display: flex;
  flex-direction: column;

  flex: 1;
  min-height: 0;
}

.viewer-wrap {
  flex: 1;
  min-height: 0;
}

.drawing-stage {
  width: 100%;
  height: 100%;
  border: 1px solid var(--line2);
  border-radius: 10px;
  position: relative;
  overflow: auto;
  background: #fff;
  text-align: center;
  user-select: none;
}

.image-box {
  position: relative;
  box-sizing: border-box;
  display: inline-block;
  vertical-align: top;
}

.preview-full {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: fill;
  pointer-events: none;
  user-select: none;
}

.drawing-empty {
  height: 100%;
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 14px;
}

.logger-marker {
  position: absolute;
  z-index: 20;
  transform: translate(-50%, -100%);
  cursor: move;
}

.logger-dot {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  background: #ef4444;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  margin: 0 auto;
}

.logger-label {
  margin-top: 3px;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.88);
  color: #ffffff;
  font-size: 0.8rem;
  line-height: 1.2;
  white-space: nowrap;
}

.context-menu {
  position: fixed;
  z-index: 2000;
  min-width: 130px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.context-item {
  width: 100%;
  height: 38px;
  border: none;
  background: #ffffff;
  text-align: left;
  padding: 0 14px;
  cursor: pointer;
  font-size: 0.95rem;
}

.context-item:hover {
  background: #f3f4f6;
}

.context-item:disabled {
  color: #9ca3af;
  cursor: not-allowed;
  background: #ffffff;
}

.context-item.danger {
  color: #dc2626;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(0, 0, 0, 0.18);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px;
  overflow: auto;
}

.logger-modal {
  width: min(520px, calc(100vw - 48px));
  max-height: calc(100vh - 48px);
  overflow: auto;
  background: #ffffff;
  border-radius: 14px;
  padding: 20px;
  box-sizing: border-box;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.18);
  margin: auto 0;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 18px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 16px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-row.full {
  grid-column: 1 / -1;
}

.form-row label {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
}

.form-control {
  width: 100%;
  height: 50px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 12px;
  box-sizing: border-box;
  font-size: 0.9rem;
  background: #ffffff;
}

.textarea {
  height: 110px;
  padding-top: 10px;
  resize: none;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
}

.bottom-actions-page {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid var(--line2);
  background: #ffffff;
  cursor: pointer;
  font-size: 14px;
  color: var(--text);
}

.btn.secondary {
  background: #ffffff;
}

.btn.primary {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #ffffff;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

.logger-card-list {
  width: 320px;
  height: 220px;
  max-width: 100%;
  margin: 0 auto;

  overflow-y: auto;
  overflow-x: hidden;

  display: flex;
  flex-direction: column;
  gap: 6px;

  padding: 6px;

  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #f8fafc;
  box-sizing: border-box;
}

.logger-card {
  width: 100%;
  box-sizing: border-box;

  padding: 8px 10px;

  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;

  cursor: pointer;

  transition:
    background-color .15s ease,
    border-color .15s ease,
    box-shadow .15s ease;
}

.logger-card:hover {
  background: #f8fafc;
  border-color: #f0b35a;
  box-shadow: inset 3px 0 0 #e58a13;
}

.logger-card.active {
  background: #fff7ed;
  border-color: #e58a13;
  box-shadow: inset 3px 0 0 #e58a13;
}

.logger-card-title {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 6px;
  text-align: center;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.logger-card-info {
  display: grid;
  grid-template-columns: 74px minmax(0, 1fr);
  gap: 4px;

  font-size: 12px;
  margin-bottom: 2px;
}

.info-label {
  color: #6b7280;
  font-weight: 600;
}

.info-value {
  color: #111827;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.logger-card-empty {
  padding: 20px;
  text-align: center;
  color: #9ca3af;
}

.logger-filter-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.logger-filter-btn {
  height: 34px;
  padding: 0 14px;

  border: 1px solid #d1d5db;
  border-radius: 8px;

  background: #ffffff;
  cursor: pointer;

  font-size: 13px;
  font-weight: 600;

  transition: all .15s ease;
}

.logger-filter-btn:hover {
  border-color: #e58a13;
}

.logger-filter-btn.active {
  background: #2563eb;
  border-color: #2563eb;
  color: #ffffff;
}

@media (max-width: 768px) {
  .logger-card-list {
    width: 250px;
    height: 250px;
  }

  .logger-card-info {
    grid-template-columns: 64px minmax(0, 1fr);
    font-size: 11px;
  }

  .logger-card-title {
    font-size: 12px;
  }
}
</style>